import { useMemo } from 'react'
import { fromPairs } from 'lodash'

import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import useToggleSdkContext from 'utils/useToogleSdk'
import { PeriodContext } from 'app/KinerjaShared/hooks/usePeriodContext'
import { useAPIGetCurrentPeriodV2 } from 'api/kinerja/useAPIKinerja'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { formatDate } from 'utils/datetime'
import { getSession } from 'utils/auth'
import { IS_DEVELOPMENT } from 'app/KinerjaShared/constants/env'

interface Props {
  disableApi?: boolean
  children: React.ReactNode
}

const defaultYear = Math.max(new Date().getFullYear(), 2024)

const defaultPeriod = {
  id: '',
  isApproved: false,
  startDate: formatDate(new Date(defaultYear, 0, 1), 'yyyy-MM-dd'),
  endDate: formatDate(new Date(defaultYear, 5, 1), 'yyyy-MM-dd'),
  period: 1,
  periodeKinerja: [],
}

type PeriodStorage = Record<string, string>
const KINERJA_PERIOD_STORAGE_KEY = 'kinerja-period-data'

/**
 * Provides the current period information.
 *
 * @param {boolean} props.disableApi - Disable the API call to get the current period.
 */
export default function PeriodProvider({
  disableApi = false,
  children,
}: Props) {
  const session = getSession()
  const toggles = useToggleSdkContext()
  const isToggleEnabled = toggles?.['kinerja-use-multiple-period'] ?? false
  const isMultiplePeriodEnabled = isToggleEnabled

  const [allValues, setValues] = useLocalStorage(KINERJA_PERIOD_STORAGE_KEY, {})
  const values = allValues as PeriodStorage

  const periodId = useMemo(
    () => values[session?.user?.id] ?? '',
    [values, session?.user?.id]
  )
  const setValue = (periodId: string) => {
    const lastValues = fromPairs(Object.entries(values).slice(-10))

    setValues({
      ...lastValues,
      [session?.user?.id]: periodId,
    })
  }

  const { data, isFetched, isLoading } = useAPIGetCurrentPeriodV2({
    useErrorBoundary: true,
    enabled: !disableApi,
    staleTime: 60 * 60 * 1000, // 1 hour
    onSuccess(data) {
      if (data.data.length > 0 && !periodId) {
        const firstPeriod = data.data[0]
        setValue(firstPeriod.id)
      }
    },
  })

  const period = useMemo(() => {
    return data?.data?.find((item) => item.id === periodId) ?? defaultPeriod
  }, [data, periodId])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center h-screen">
        <Spinner data-testid="loading-spinner-period" />
        {IS_DEVELOPMENT && <Text>Loading period data...</Text>}
      </div>
    )
  }

  return (
    <PeriodContext.Provider
      value={{
        isEnabled: isMultiplePeriodEnabled,
        isHavePeriod: Boolean(periodId),
        periodYear: new Date(period.startDate).getFullYear(),
        periodNo: period.period,
        period,
        setPeriod: ({ id }) => setValue(id),
        isFetched,

        // Data period
        periods: data?.data,

        // Helpers
        getPeriod: ({ periodYear, periodNo }) => {
          return data?.data?.find(
            (p) =>
              new Date(p.startDate).getFullYear() === periodYear &&
              p.period === periodNo
          )
        },
      }}
    >
      {children}
    </PeriodContext.Provider>
  )
}
